import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 22, 40, 0.95);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.base};
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows['2xl']};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const ModalHeader = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.lg}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => `${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0`};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  transition: all ${({ theme }) => theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.muted};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
`;

const Paragraph = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  &:last-child {
    margin-bottom: 0;
  }
`;

const List = styled.ul`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const ListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactInfo = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

const ContactTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ContactText = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const PrivacyPolicy = ({ isOpen, onClose }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    const handleKeyDownEffect = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDownEffect);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDownEffect);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Privacy Policy</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close Privacy Policy">
            ×
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <Section>
            <Paragraph>
              <strong>Effective Date:</strong> July 22, 2025
            </Paragraph>
            <Paragraph>
              <strong>Last Updated:</strong> July 22, 2025
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Introduction</SectionTitle>
            <Paragraph>
              Antony Haines Consulting ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website antonyhaines.com and engage with our fractional CTO consulting services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Information We Collect</SectionTitle>
            
            <ContactTitle>Personal Information</ContactTitle>
            <Paragraph>
              We may collect personal information that you voluntarily provide to us when:
            </Paragraph>
            <List>
              <ListItem>Filling out contact forms on our website</ListItem>
              <ListItem>Scheduling Zoom consultations through Calendly integration</ListItem>
              <ListItem>Subscribing to our newsletters or updates</ListItem>
              <ListItem>Engaging with our consulting services</ListItem>
              <ListItem>Communicating with us via email or phone</ListItem>
            </List>

            <ContactTitle>Information Collected Automatically</ContactTitle>
            <Paragraph>
              When you visit our website, we may automatically collect certain information:
            </Paragraph>
            <List>
              <ListItem>IP address and geographic location</ListItem>
              <ListItem>Browser type and operating system</ListItem>
              <ListItem>Pages visited and time spent on site</ListItem>
              <ListItem>Referring websites and search terms</ListItem>
              <ListItem>Device information and screen resolution</ListItem>
            </List>

            <ContactTitle>Cookies and Tracking Technologies</ContactTitle>
            <Paragraph>
              We use cookies and similar tracking technologies to enhance your experience:
            </Paragraph>
            <List>
              <ListItem>Essential cookies for website functionality</ListItem>
              <ListItem>Analytics cookies to understand website usage</ListItem>
              <ListItem>Calendly integration cookies for scheduling services</ListItem>
              <ListItem>Performance cookies to optimize website speed</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>How We Use Your Information</SectionTitle>
            <Paragraph>
              We use the collected information for the following purposes:
            </Paragraph>
            <List>
              <ListItem>Providing and improving our fractional CTO consulting services</ListItem>
              <ListItem>Responding to your inquiries and Zoom consultation requests</ListItem>
              <ListItem>Scheduling and managing consulting engagements</ListItem>
              <ListItem>Sending relevant business communications and updates</ListItem>
              <ListItem>Analyzing website performance and user behavior</ListItem>
              <ListItem>Complying with legal obligations and protecting our rights</ListItem>
              <ListItem>Preventing fraud and ensuring website security</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Information Sharing and Disclosure</SectionTitle>
            <Paragraph>
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </Paragraph>
            <List>
              <ListItem><strong>Service Providers:</strong> Trusted third parties who assist in operating our website and providing services (e.g., Calendly for scheduling)</ListItem>
              <ListItem><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</ListItem>
              <ListItem><strong>Business Protection:</strong> To protect our rights, property, or safety, or that of our clients</ListItem>
              <ListItem><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of business assets</ListItem>
              <ListItem><strong>Consent:</strong> With your explicit consent for any other purpose</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Security</SectionTitle>
            <Paragraph>
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction. 
              These measures include:
            </Paragraph>
            <List>
              <ListItem>SSL encryption for data transmission</ListItem>
              <ListItem>Secure hosting infrastructure</ListItem>
              <ListItem>Regular security assessments and updates</ListItem>
              <ListItem>Access controls and authentication procedures</ListItem>
              <ListItem>Employee training on data protection practices</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Your Rights and Choices</SectionTitle>
            <Paragraph>
              You have the following rights regarding your personal information:
            </Paragraph>
            <List>
              <ListItem><strong>Access:</strong> Request access to your personal information we hold</ListItem>
              <ListItem><strong>Correction:</strong> Request correction of inaccurate personal information</ListItem>
              <ListItem><strong>Deletion:</strong> Request deletion of your personal information</ListItem>
              <ListItem><strong>Opt-out:</strong> Unsubscribe from marketing communications</ListItem>
              <ListItem><strong>Cookie Control:</strong> Manage cookie preferences through your browser settings</ListItem>
              <ListItem><strong>Data Portability:</strong> Request a copy of your data in a machine-readable format</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Third-Party Services</SectionTitle>
            <Paragraph>
              Our website integrates with third-party services that have their own privacy policies:
            </Paragraph>
            <List>
              <ListItem><strong>Calendly:</strong> For Zoom consultation scheduling (subject to Calendly's privacy policy)</ListItem>
              <ListItem><strong>Web Analytics:</strong> For understanding website usage patterns</ListItem>
              <ListItem><strong>Email Services:</strong> For business communications</ListItem>
            </List>
            <Paragraph>
              We encourage you to review the privacy policies of these third-party services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Data Retention</SectionTitle>
            <Paragraph>
              We retain your personal information only as long as necessary to fulfill the purposes 
              outlined in this Privacy Policy, comply with legal obligations, resolve disputes, 
              and enforce our agreements. Client consultation data is typically retained for 
              7 years for business and legal compliance purposes.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Children's Privacy</SectionTitle>
            <Paragraph>
              Our services are designed for business professionals and are not directed to children 
              under 13. We do not knowingly collect personal information from children under 13. 
              If we become aware that we have collected such information, we will take steps to delete it promptly.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>International Data Transfers</SectionTitle>
            <Paragraph>
              Your information may be transferred to and processed in countries other than your country 
              of residence. We ensure that such transfers comply with applicable data protection laws 
              and implement appropriate safeguards to protect your information.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Changes to This Privacy Policy</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time to reflect changes in our practices 
              or legal requirements. We will notify you of any material changes by posting the updated 
              policy on our website with a new effective date. We encourage you to review this policy periodically.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Information</SectionTitle>
            <ContactInfo>
              <ContactTitle>Questions About This Privacy Policy?</ContactTitle>
              <ContactText>
                If you have any questions, concerns, or requests regarding this Privacy Policy or 
                our data practices, please contact us:
              </ContactText>
              <ContactText style={{ marginTop: '8px' }}>
                <strong>Email:</strong> antony@antonyhaines.com<br />
                <strong>Website:</strong> antonyhaines.com<br />
                <strong>Business:</strong> Antony Haines Consulting - Fractional CTO Services
              </ContactText>
            </ContactInfo>
          </Section>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PrivacyPolicy;